import type { Counter, Direction } from "~/types";

const MS_PER_DAY = 86_400_000;

/** Parses a yyyy-mm-dd string as a local calendar date at midnight. */
export function parseDateOnly(value: string): Date {
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, (month ?? 1) - 1, day ?? 1);
}

/** Formats a Date as yyyy-mm-dd using its local calendar date. */
export function formatDateOnly(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function today(): string {
  return formatDateOnly(new Date());
}

/** Whole calendar days from `from` to `to` (positive when `to` is later). */
export function daysBetweenDates(from: Date, to: Date): number {
  const fromUTC = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const toUTC = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((toUTC - fromUTC) / MS_PER_DAY);
}

export type CounterValue = {
  /** Always >= 0 — the magnitude to display. */
  days: number;
  /**
   * true when the counter is showing the "wrong way" round for its mode:
   * an "up" (since) counter whose date hasn't arrived yet, or a "down"
   * (until) counter whose date has already passed.
   */
  isPending: boolean;
};

export function computeCounterValue(
  counter: Pick<Counter, "direction" | "targetDate">,
  now: Date = new Date(),
): CounterValue {
  const target = parseDateOnly(counter.targetDate);
  if (counter.direction === "up") {
    const elapsed = daysBetweenDates(target, now);
    return { days: Math.abs(elapsed), isPending: elapsed < 0 };
  }
  const remaining = daysBetweenDates(now, target);
  return { days: Math.abs(remaining), isPending: remaining < 0 };
}

/** Short, lowercase phrase describing a counter's value, e.g. "since" / "to go" / "ago" / "to start". */
export function describeCounterValue(
  direction: Direction,
  value: CounterValue,
): string {
  if (direction === "up") {
    return value.isPending ? "to start" : "days ago";
  }
  return value.isPending ? "days overdue" : "to go";
}

export function formatDayCount(days: number): string {
  return days.toLocaleString("en-US");
}
